<template>
  <label :class="['mic-input', {inactive: !active}]">
    <span v-if="label && active" class="mic-input__label">{{ label }}     <div
      v-if="phoneNumberTooltip"
      class="mic-input__tooltip"
    >
      <span class="mic-input__tooltip__text">{{ phoneNumberTooltipText }}</span>
      <v-popover
        placement="bottom-end"
        offset="8"
        trigger="click hover focus"
      >
        <div

          class="mic-input__tooltip__icon"
        >
          {{ tooltipCharacter }}
        </div>

        <template #popover>
          {{ phoneNumberTooltipContent }}
        </template>
      </v-popover>
    </div></span>
    <span v-if="!active" class="mic-input__label--inactive" />
    <div :class="{ 'mic-input__input-container--error': dirtyError, 'mic-input__input-container--success': isValid, 'mic-input__input-container--required': required && !isValid && !dirtyError }" class="mic-input__input-container">
      <BaseInput
        ref="input"
        :value="value"
        v-bind="$attrs"
        :type="type"
        class="mic-input__input"
        tabindex="0"
        @blur="handleBlur"
        @input="input"
      />
      <span v-if="!active" class="mic-input__input--inactive" />
      <img
        v-if="!hideValidationIcons && dirtyError"
        :src="require(`../assets/icons/${errorIcon}`)"
        alt="error icon"
      >
      <img
        v-else-if="!hideValidationIcons && isValid"
        :src="require(`../assets/icons/${successIcon}`)"
        alt="input valid checkmark icon"
      >
      <slot v-else />
    </div>
    <span class="mic-input__description">{{ description }}</span>
    <span v-if="dirtyError" class="mic-input__error">{{ error }}</span>
  </label>
</template>

<script>
import BaseInput from './BaseInput'

export default {
  name: 'MicInput',
  components: {
    BaseInput
  },
  inheritAttrs: false,
  props: {
    label: {
      type: String,
      default: ''
    },
    description: {
      type: String,
      default: ''
    },
    value: {
      type: String,
      default: ''
    },
    type: {
      type: String,
      default: 'text'
    },
    error: {
      type: String,
      default: ''
    },
    isValid: {
      type: Boolean,
      default: false
    },
    active: {
      type: Boolean,
      default: true
    },
    hideValidationIcons: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    errorIcon: {
      type: String,
      default: 'error-circle.svg'
    },
    successIcon: {
      type: String,
      default: 'check-circle-green.svg'
    },
    phoneNumberTooltip: {
      type: Boolean,
      deafult: false
    },
    tooltipCharacter: {
      type: String,
      default: 'i'
    },
    phoneNumberTooltipText: {
      type: String,
      default: ''
    },
    phoneNumberTooltipContent: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      dirty: false
    }
  },

  computed: {
    dirtyError () {
      return this.error && this.dirty
    }
  },

  methods: {
    handleBlur () {
      this.dirty = true
    },
    input (value) {
      this.$emit('input', value)
    }
  }
}
</script>

<style lang="scss" scoped>
.mic-input {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: $spacing-0;

  &.inactive {
    pointer-events: none;
  }

  &__label {
    color: $input-label-text;
    display: $input-label-display;
    font-weight: $input-label-font-weight;
    font-size: $input-label-text-size;
    margin-bottom: $input-label-margin-bottom;
  }

  &__label--inactive {
    height: 18px;
    width: 200px;
    background-color: $input-label-bg;
    margin: 4px 0 $spacing-16;
  }

    &__tooltip {
      display: flex;
      justify-content: $contact-details-capture-tooltip-justify-content;
      align-items: center;
      margin-left: auto;

      &__text {
        font-size: $contact-details-capture-tooltip-text-size;
        color: $contact-details-capture-tooltip-text-color;
        font-weight: $contact-details-capture-tooltip-font-weight;
      }

      &__icon {
        text-align: center;
        height: $contact-details-capture-tooltip-icon-height;
        width: $contact-details-capture-tooltip-icon-width;
        min-width: $contact-details-capture-tooltip-icon-width;
        margin: $spacing-0 $spacing-0 $spacing-0 $spacing-10;
        padding: $spacing-0;
        border: $contact-details-capture-tooltip-border;
        border-radius: 50%;
        font-size: $contact-details-capture-tooltip-icon-font-size;
        font-weight: 700;
        cursor: help;
        color: $contact-details-capture-tooltip-icon-color;
        background-color: $contact-details-capture-tooltip-icon-bg;
        font-family: $contact-details-capture-tooltip-icon-font;
        display: flex;
        align-items: center;
        justify-content: center;
      }

    }

  &__input-container {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    height: 56px;
    padding: $spacing-16 $spacing-20;
    border: 1px solid $input-container-border;
    border-radius: $input-border-radius;
    background-color: $white;
    box-shadow: $input-box-shadow;

    &--error {
       border: $input-container-border-error;
       box-shadow: $input-box-shadow-error;
       background-color: $input-container-bg-error;
       input { background-color: $input-container-bg-error; }
    }

    &--success {
      border: $input-container-border-success;
      box-shadow: $input-box-shadow-success;
      background-color: $input-container-bg-success;
      input { background-color: $input-container-bg-success; }
    }

    &--required {
      border: $input-container-border-required;
      box-shadow: $input-box-shadow-required;
      background-color: $input-container-bg-required;
      input { background-color: $input-container-bg-required; }
    }
  }

  &__input {
    flex-grow: 1;
    border: 0;
    outline: none;
    color: $text-color;
    font-size: 16px;
    line-height: 16px;
    font-weight: $input-font-weight;

    &::placeholder {
      font-weight: normal;
      color: rgba($input-text, 0.4);
    }
  }

  &__input--inactive {
    height: 18px;
    width: 200px;
    background-color: $input-bg;
    margin-bottom: $spacing-12;
    position: absolute;

  }

  &__description {
    font-size: 14px;
  }

  &__error {
    padding-top: $input-padding-top-error;
    font-family: $font-family-bold;
    font-size: 16px;
    font-weight: 500;
    color: $error-red;
    text-align: left;
  }
}
</style>
